/**
 * Database Tab — sub-tab container for Settings page.
 * Renders sub-tab navigation with: "Backup & Restore", "Import Flights XLS", "Import CSV".
 * Lazy-mounts each sub-tab's component on first activation.
 *
 * Requirements: 14.2, 14.3
 */

import { createBackupManager } from './backup-manager.js';
import { createXLSImport } from './xls-import.js';
import { createCSVImport } from './csv-import.js';

const SUB_TABS = [
  { key: 'backup-restore', label: 'Backup & Restore', factory: createBackupManager },
  { key: 'import-xls', label: 'Import Flights XLS', factory: createXLSImport },
  { key: 'import-csv', label: 'Import CSV', factory: createCSVImport },
];

/**
 * Creates the Database tab panel with sub-tab navigation.
 * Lazy-loads each sub-tab's content on first activation.
 * @returns {HTMLElement}
 */
export function createDatabaseTab() {
  const container = document.createElement('div');
  container.className = 'database-tab';

  // Sub-tab navigation
  const nav = document.createElement('nav');
  nav.className = 'settings-subtabs__nav';
  nav.setAttribute('role', 'tablist');

  // Sub-tab panels container
  const panelsContainer = document.createElement('div');
  panelsContainer.className = 'settings-subtabs__content';

  // Track which panels have been mounted
  const mounted = {};

  SUB_TABS.forEach((tab, index) => {
    // Create nav button
    const btn = document.createElement('button');
    btn.className = 'settings-subtabs__tab';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('data-subtab', tab.key);
    btn.textContent = tab.label;

    if (index === 0) {
      btn.classList.add('settings-subtabs__tab--active');
      btn.setAttribute('aria-selected', 'true');
    } else {
      btn.setAttribute('aria-selected', 'false');
    }

    nav.appendChild(btn);

    // Create panel
    const panel = document.createElement('div');
    panel.className = 'settings-subtabs__panel';
    panel.id = `subtab-${tab.key}`;
    panel.setAttribute('role', 'tabpanel');

    if (index === 0) {
      panel.classList.add('settings-subtabs__panel--active');
    }

    panelsContainer.appendChild(panel);
  });

  container.appendChild(nav);
  container.appendChild(panelsContainer);

  // Lazy-mount the default (first) sub-tab
  lazyMount(SUB_TABS[0], panelsContainer, mounted);

  // Sub-tab switching
  nav.addEventListener('click', (e) => {
    const btn = e.target.closest('.settings-subtabs__tab');
    if (!btn) return;

    const key = btn.getAttribute('data-subtab');
    const tabDef = SUB_TABS.find((t) => t.key === key);
    if (!tabDef) return;

    // Deactivate all
    nav.querySelectorAll('.settings-subtabs__tab').forEach((t) => {
      t.classList.remove('settings-subtabs__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    panelsContainer.querySelectorAll('.settings-subtabs__panel').forEach((p) => {
      p.classList.remove('settings-subtabs__panel--active');
    });

    // Activate clicked
    btn.classList.add('settings-subtabs__tab--active');
    btn.setAttribute('aria-selected', 'true');
    const panel = panelsContainer.querySelector(`#subtab-${key}`);
    if (panel) {
      panel.classList.add('settings-subtabs__panel--active');
    }

    // Lazy-mount on first activation
    lazyMount(tabDef, panelsContainer, mounted);
  });

  return container;
}

/**
 * Lazy-mount a sub-tab's component into its panel if not already mounted.
 * @param {{ key: string, factory: Function }} tabDef
 * @param {HTMLElement} panelsContainer
 * @param {Object} mounted - Track mounted state
 */
function lazyMount(tabDef, panelsContainer, mounted) {
  if (mounted[tabDef.key]) return;

  const panel = panelsContainer.querySelector(`#subtab-${tabDef.key}`);
  if (!panel) return;

  try {
    const component = tabDef.factory();
    panel.appendChild(component);
    mounted[tabDef.key] = true;
  } catch (err) {
    panel.innerHTML = `<p class="error">Failed to load ${tabDef.label}: ${err.message}</p>`;
  }
}
